import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Avatar, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import {
    AddBusinessRounded, CampaignRounded, CloudDownloadRounded,
    DashboardRounded, FactoryRounded, GroupsRounded, ImageRounded, Inventory2Rounded,
    LocalShippingRounded, ManageAccountsRounded, NfcRounded, NotificationsRounded,
    PaidRounded, PeopleRounded, PublishedWithChangesRounded, QueryStatsRounded, SchemaRounded,
    StorefrontRounded, TaskAltRounded, HomeRounded, LogoutRounded, WeekendRounded, DevicesRounded,
} from '@mui/icons-material';
import styles from './adminPanel.module.scss';
import ProducersComponent from './Components/ProducersComponent/ProducersComponent';
import CollectionsComponent from './Components/CollectionsComponent/CollectionsComponent';
import PromotionalFurnitureComponent from './Components/PromotionalFurnitureComponent/PromotionalFurnitureComponent';
import Charts from './Components/Charts/Charts';
import TasksComponent from './Components/TasksComponent/TasksComponent';
import ImageSliderForm from '../../../components/forms/imageSlider/ImageSliderForm/ImageSliderForm';
import importedFurnitureController from '../../../Api/importedFurnitureController';
import storageController from '../../../Api/storageController';
import { useUser } from '../../../hooks/useUser';
import UsersPage from '../Users/UsersPage';
import EmployeesPage from '../Employees/EmployeesPage';
import RfidTagsPage from '../Rfid/RfidTagsPage';
import NotificationsPage from '../Notifications/NotificationsPage';
import NonWorkingDays from '../NonWorkingDays/NonWorkingDays';
import FactoryDeliveryNotices from '../FactoryDeliveryNotices/FactoryDeliveryNotices';
import InvoicesPage from '../../InvoicesPage/InvoicesPage';
import TimeTracker from '../../TimeTracker/TimeTracker';
import ModuleEditor from '../ModuleEditor/ModuleEditor';
import MissingDimensionModuleEditor from '../ModuleEditor/MissingDimensionModuleEditor';
import ufoLogo from '../../../assets/img/ufo.png';
import AddFurnitureForm from '../../../components/forms/furniture/AddFurnitureForm/AddFurnitureForm';
import { useModal } from '../../../hooks/useModal';
import FurnitureStatistics from './Components/FurnitureStatistics/FurnitureStatistics';
import KiosksPage from '../Kiosks/KiosksPage';
import ApplicationUpdates from '../ApplicationUpdates/ApplicationUpdates';

const sections = [
    {
        label: 'Pulpit',
        items: [
            { id: 'overview', title: 'Podsumowanie', description: 'Najważniejsze narzędzia i stan dostępu.', permission: 'admin.access', icon: DashboardRounded },
            { id: 'statistics', title: 'Statystyki', description: 'Odsłony strony i popularność konkretnych mebli.', permission: 'statistics.read', icon: QueryStatsRounded },
        ],
    },
    {
        label: 'Katalog i treści',
        items: [
            { id: 'clarnet', title: 'CLARNET', description: 'Oddzielne narzędzie do wprowadzania danych mebli.', permission: 'furniture.create', path: '/clarnet', icon: AddBusinessRounded },
            { id: 'single-furniture', title: 'Dodaj mebel', description: 'Formularz dodawania pojedynczego mebla.', permission: 'furniture.create', icon: WeekendRounded },
            { id: 'modules', title: 'Edytor modułów', description: 'Uzupełnianie brakujących typów modułów.', permission: 'furniture.update', icon: SchemaRounded },
            { id: 'dimensions', title: 'Brakujące wymiary', description: 'Uzupełnianie wymiarów modułów.', permission: 'furniture.update', icon: Inventory2Rounded },
            { id: 'producers', title: 'Producenci', description: 'Lista, dodawanie, edycja i eksport.', permission: 'producers.read', icon: FactoryRounded },
            { id: 'collections', title: 'Kolekcje', description: 'Kolekcje producentów oraz zdjęcia.', permission: 'collections.read', icon: StorefrontRounded },
            { id: 'promotions', title: 'Promocje', description: 'Oferta promocyjna i jej widoczność.', permission: 'promotions.read', icon: CampaignRounded },
            { id: 'slider', title: 'Slider strony', description: 'Slajdy, kolejność i publikacja.', permission: 'imageSlider.read', icon: ImageRounded },
            { id: 'imports', title: 'Meble importowane', description: 'Podgląd oraz ręczna synchronizacja.', permission: 'importedFurniture.read', icon: PublishedWithChangesRounded },
        ],
    },
    {
        label: 'Firma i obsługa',
        items: [
            { id: 'invoices', title: 'Proformy', description: 'Dokumenty, edycja i wysyłka.', permission: 'invoices.read', icon: PaidRounded },
            { id: 'deliveries', title: 'Awizacje dostaw', description: 'Terminy, fabryki i odbiory.', permission: 'deliveryNotices.read', icon: LocalShippingRounded },
            { id: 'time', title: 'Rejestracja czasu', description: 'Skany i raporty obecności.', permission: 'timeTracking.read', icon: TaskAltRounded },
            { id: 'employees', title: 'Pracownicy', description: 'Dane pracowników.', permission: 'employees.read', icon: PeopleRounded },
            { id: 'rfid', title: 'Karty RFID', description: 'Tagi i przypisania.', permission: 'rfid.read', icon: NfcRounded },
            { id: 'notifications', title: 'Powiadomienia', description: 'Komunikaty dla aplikacji.', permission: 'notifications.read', icon: NotificationsRounded },
            { id: 'days-off', title: 'Dni wolne fabryk', description: 'Kalendarze dostępności.', permission: 'nonWorkingDays.read', icon: FactoryRounded },
        ],
    },
    {
        label: 'System',
        items: [
            { id: 'remanent', title: 'Remanent', description: 'Aktualizacja serwera remanentu i log operacji.', permission: 'remanent.manage', icon: Inventory2Rounded },
            { id: 'website-update', title: 'Aktualizacja strony UFO', description: 'Pobranie i uruchomienie aktualnej wersji strony.', permission: 'website.update', icon: PublishedWithChangesRounded },
            { id: 'kiosks', title: 'Kioski', description: 'Urządzenia, status online i synchronizacja.', permission: 'kiosks.read', icon: DevicesRounded },
            { id: 'users', title: 'Użytkownicy', description: 'Konta, role i uprawnienia.', permission: 'users.read', icon: ManageAccountsRounded },
            { id: 'tasks', title: 'Zadania', description: 'Plan pracy zespołu.', permission: 'tasks.read', icon: GroupsRounded },
            { id: 'storage', title: 'Archiwum plików', description: 'Kopia katalogu storage.', permission: 'storage.read', icon: CloudDownloadRounded },
        ],
    },
];

const allItems = sections.flatMap((section) => section.items);

export default function AdminPanel() {
    const location = useLocation();
    const navigate = useNavigate();
    const user = useUser();
    const modal = useModal();
    const requestedView = new URLSearchParams(location.search).get('sekcja') || 'overview';
    const [message, setMessage] = useState(null);
    const [busy, setBusy] = useState('');

    useEffect(() => { document.title = 'Panel administratora'; }, []);

    const visibleSections = sections.map((section) => ({
        ...section,
        items: section.items.filter((item) => user.hasPermission(item.permission)),
    })).filter((section) => section.items.length);

    const activeItem = allItems.find((item) => item.id === requestedView && user.hasPermission(item.permission)) || allItems[0];

    const selectItem = (item) => {
        setMessage(null);
        if (item.id === 'single-furniture') {
            navigate('/adminPanel?sekcja=single-furniture', { replace: true });
            modal.open(<AddFurnitureForm />);
            return;
        }
        if (item.path) return navigate(item.path);
        navigate(`/adminPanel?sekcja=${item.id}`, { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const updateImports = async () => {
        setBusy('imports');
        const result = await importedFurnitureController.forceUpdateImportedFurniture();
        setMessage({ severity: result.ok ? 'success' : 'error', text: result.message });
        setBusy('');
    };

    const renderContent = () => {
        switch (activeItem.id) {
            case 'remanent': return <ApplicationUpdates target="remanent" />;
            case 'website-update': return <ApplicationUpdates target="website" />;
            case 'producers': return <ProducersComponent />;
            case 'collections': return <CollectionsComponent />;
            case 'promotions': return <PromotionalFurnitureComponent />;
            case 'slider': return <ImageSliderForm embedded />;
            case 'modules': return <ModuleEditor />;
            case 'dimensions': return <MissingDimensionModuleEditor />;
            case 'single-furniture': return <ActionView icon={WeekendRounded} title="Dodaj pojedynczy mebel" description="Użyj istniejącego formularza z katalogu mebli. Uzupełnij producenta, kolekcję, szczegóły, zdjęcia, pomieszczenia i kategorie.">
                <Button variant="contained" onClick={() => modal.open(<AddFurnitureForm />)}>Otwórz formularz dodawania</Button>
            </ActionView>;
            case 'tasks': return <TasksComponent />;
            case 'users': return <UsersPage embedded />;
            case 'kiosks': return <KiosksPage embedded />;
            case 'employees': return <EmployeesPage embedded />;
            case 'rfid': return <RfidTagsPage embedded />;
            case 'notifications': return <NotificationsPage embedded />;
            case 'days-off': return <NonWorkingDays embedded />;
            case 'deliveries': return <FactoryDeliveryNotices embedded />;
            case 'invoices': return <InvoicesPage embedded />;
            case 'time': return <TimeTracker embedded />;
            case 'statistics': return <FurnitureStatistics />;
            case 'imports': return <ActionView icon={PublishedWithChangesRounded} title="Meble importowane" description="Otwórz aktualną ofertę importowaną albo pobierz świeże dane z backendu.">
                <Button variant="contained" onClick={() => navigate('/meble-importowane')}>Otwórz katalog</Button>
                {user.hasPermission('importedFurniture.update') && <Button variant="outlined" disabled={busy === 'imports'} onClick={updateImports} endIcon={busy === 'imports' ? <CircularProgress size={16} /> : undefined}>Uruchom aktualizację</Button>}
            </ActionView>;
            case 'storage': return <ActionView icon={CloudDownloadRounded} title="Archiwum plików" description="Backend przygotuje archiwum ZIP całego katalogu storage.">
                <Button variant="contained" startIcon={<CloudDownloadRounded />} onClick={() => storageController.downloadStorageFolder()}>Pobierz archiwum</Button>
            </ActionView>;
            default: return <Overview user={user} />;
        }
    };

    const logout = async () => {
        await user.logout();
        navigate('/logowanie');
    };

    return <main className={styles.page}>
            <div className={styles.shell}>
                <aside className={styles.sidebar}>
                    <div className={styles.brand}>
                        <button type="button" className={styles.logoButton} onClick={() => navigate('/')} aria-label="Przejdź na stronę UFO">
                            <img src={ufoLogo} alt="UFO" />
                        </button>
                        <Box className={styles.brandTitle}><Typography fontWeight={900}>UFO Admin</Typography><Typography variant="caption">Centrum zarządzania</Typography></Box>
                    </div>
                    <Divider className={styles.sideDivider} />
                    <nav aria-label="Moduły administratora" className={styles.nav}>
                        {visibleSections.map((section) => <div key={section.label} className={styles.navSection}>
                            <Typography className={styles.navLabel}>{section.label}</Typography>
                            {section.items.map((item) => {
                                const Icon = item.icon;
                                const selected = !item.path && activeItem.id === item.id;
                                return <button key={item.id} type="button" className={`${styles.navItem} ${selected ? styles.navItemActive : ''}`} onClick={() => selectItem(item)}>
                                    <Icon className={styles.navIcon} />
                                    <span>{item.title}</span>
                                </button>;
                            })}
                        </div>)}
                    </nav>
                    <div className={styles.profile}>
                        <Avatar>{(user.currentUser?.displayName || user.currentUser?.username || 'A')[0].toUpperCase()}</Avatar>
                        <Box minWidth={0}>
                            <Typography noWrap fontWeight={800} variant="body2">{user.currentUser?.displayName || user.currentUser?.username}</Typography>
                            <Typography noWrap variant="caption">@{user.currentUser?.username || user.currentUser?.role}</Typography>
                        </Box>
                    </div>
                    <div className={styles.accountActions}>
                        <button type="button" onClick={() => navigate('/')}><HomeRounded /> Strona główna</button>
                        <button type="button" onClick={logout}><LogoutRounded /> Wyloguj</button>
                    </div>
                </aside>

                <section className={styles.main}>
                    <header className={styles.header}>
                        <Box>
                            <Typography className={styles.eyebrow}>Panel administratora</Typography>
                            <Typography component="h1" className={styles.pageTitle}>{activeItem.title}</Typography>
                            <Typography className={styles.pageDescription}>{activeItem.description}</Typography>
                        </Box>
                        <Chip label={`${visibleSections.reduce((sum, section) => sum + section.items.length, 0)} dostępnych modułów`} className={styles.moduleCount} />
                    </header>
                    {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>{message.text}</Alert>}
                    <Paper className={styles.workspace} elevation={0}>{renderContent()}</Paper>
                </section>
            </div>
        </main>;
}

function Overview({ user }) {
    return <Box className={styles.overview}>
        <section className={styles.analyticsPanel} aria-label="Statystyki strony">
            {user.hasPermission('statistics.read')
                ? <Charts dashboard />
                : <div className={styles.noAnalytics}><Typography fontWeight={900}>Statystyki są niedostępne</Typography><Typography variant="body2">Twoje konto nie ma uprawnienia do ich odczytu.</Typography></div>}
        </section>
    </Box>;
}

function ActionView({ icon: Icon, title, description, children }) {
    return <div className={styles.actionView}>
        <Avatar><Icon /></Avatar>
        <Typography component="h2">{title}</Typography>
        <Typography>{description}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={3}>{children}</Stack>
    </div>;
}
